import React, { forwardRef, useImperativeHandle, useState } from "react";
import Page from "components/UI/Page";
import MultiHeaderTable from 'components/UI/MultiHeaderTable';
import { CANNOT_SELECT_MEASUREMENT_UNIT, getFieldName, MEASUREMENTS_CHANGES_SAVED, MEASUREMENTS_HEADERS } from 'constants/measurements';
import FlexboxContainerComponent from 'components/UI/FlexboxContainer';
import Button from 'components/UI/Button';
import { Background, Main, Shade } from 'styles/themeColors';
import IssuesSelector from 'components/UI/IssuesSelector';
import { IssueSelectorError } from 'data/Generic';
import { getErrorText, validateMeasurementsGrid } from 'utils/measurements';
import { MeasurementRecord } from 'data/UnitOfMeasurement';
import { useAPI } from 'lib/API';
import { getUnitOfMeasurements, upsertUnitOfMeasurements } from 'requests/unitOfMeasurements';
import { renderToast, toast } from 'components/UI/ToastNotification';

import LoadingIndicator from 'components/UI/LoadingIndicator';
import Modal from 'components/UI/Modal';
import { ReadOnlyWrapper } from "components/common/ReadOnlyWrapper";

interface Props {
    setIsSaveDisabled: (isSaveDisabled: boolean) => void;
    setHasUnsavedChanges: (value: boolean) => void;
    readonly?: boolean;
}

const MeasurementsTable = forwardRef<{ handleSave: () => void }, Props>(
    ({ setIsSaveDisabled, setHasUnsavedChanges, readonly }, ref) => {
        const [page, setPage] = useState(0);
        const [rowsPerPage, setRowsPerPage] = useState(10);
        const [selectedRows, setSelectedRows] = useState<number[]>([]);
        const [errors, setErrors] = useState<IssueSelectorError[]>([]);
        const [order, setOrder] = useState<'asc' | 'desc'>('asc');
        const [orderBy, setOrderBy] = useState<string>('id');
        const [showErrors, setShowErrors] = useState(false);
        const [updated, setUpdated] = useState<number[]>([]);
        const [deleted, setDeleted] = useState<number[]>([]);
        const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);

        const [data, setData] = useState<MeasurementRecord[]>([]);
        const [originalData, setOriginalData] = useState<MeasurementRecord[]>([]);

        const getMeasurementsReq = useAPI({
            deferFn: getUnitOfMeasurements,
            onResolve: (result: MeasurementRecord[]) => {
                setOrder('asc');
                setOrderBy('id');
                setShowErrors(false);
                setPage(0);
                setSelectedRows([]);

                const mappedData = result.map(x => { return { ...x, disableDelete: x.isMappedToProduct } });
                setOriginalData(mappedData);
                setData(mappedData);
                setIsSaveDisabled(false);
            },
            onReject: (err: any) => {
                renderToast(toast.TYPE.ERROR, err.message);
                setIsSaveDisabled(false);
            }
        });

        const upsertMeasurementsReq = useAPI({
            deferFn: upsertUnitOfMeasurements,
            onResolve: (result: any) => {
                renderToast(toast.TYPE.SUCCESS, MEASUREMENTS_CHANGES_SAVED);
                getMeasurementsReq.run();
            },
            onReject: (err: any) => {
                renderToast(toast.TYPE.ERROR, err.message);
                setIsSaveDisabled(false);
            }
        });

        React.useEffect(() => {
            setIsSaveDisabled(true);
            getMeasurementsReq.run();
        }, []);

        const handleSelectAll = (checked: boolean) => {
            setSelectedRows(checked ? data?.filter(v => !v.disableDelete)?.map((v) => v.id) : []);
        };

        const handleSelect = (id: number) => {
            setSelectedRows((prev) =>
                prev.includes(id) ? prev.filter((rowId) => rowId !== id) : [...prev, id]
            );
        };
        const handleSort = (column: string) => {
            const isAsc = orderBy === column && order === 'asc';
            setOrder(isAsc ? 'desc' : 'asc');
            setOrderBy(column);

            setData(prevData =>
                [...prevData].sort((a, b) => {
                    const aVal = a[column];
                    const bVal = b[column];

                    const comparison = aVal.localeCompare(bVal);
                    return isAsc ? -comparison : comparison;
                })
            );
        };


        const generateId = () => {
            if (!data || data.length === 0) return 1;

            const maxId = Math.max(...data.map(item => item.id || 0));
            return maxId + 1;
        };

        const getNewRecord = () => {
            return {
                id: generateId(),
                displayName: '',
                shortName: '',
                fullName: '',
                variations: '',
                isNew: true,
                disableDelete: false
            }
        }

        const handleAdd = () => {
            setData([
                getNewRecord(),
                ...data
            ]);
            setPage(0);
        };

        const handleChange = (id: number, value: any, column: string) => {
            if (!updated.includes(id) && data.find(x => x.id === id && !x.isNew)) {
                setUpdated([...updated, id]);
            }

            setData(prevData =>
                prevData.map(record =>
                    record.id === id ? { ...record, [column]: value } : record
                )
            );
        };

        const handleDelete = () => {
            const actualDeleted = selectedRows.filter(x => data.find(y => y.id === x && !y.isNew))
            setDeleted([...deleted, ...actualDeleted]);
            setData(data.filter(x => !selectedRows.includes(x.id)));
            setSelectedRows([]);
            setShowDeleteModal(false);
        };

        const checkUpdates = () => {
            const sortById = (arr: any[]) => [...arr].sort((a, b) => a.id - b.id);

            const sortedData = sortById(data);
            const sortedOriginalData = sortById(originalData);

            if (JSON.stringify(sortedData) === JSON.stringify(sortedOriginalData)) {
                setHasUnsavedChanges(false);
            } else {
                setHasUnsavedChanges(true);
            }
        };

        React.useEffect(() => {
            if (data.length > 0) {
                const dataErrors = validateMeasurementsGrid(data);
                setErrors(dataErrors);
                setIsSaveDisabled(dataErrors.length > 0);

                checkUpdates();
            }
        }, [data]);

        React.useEffect(() => {
            if (page > Math.ceil(data.length / rowsPerPage) - 1) {
                setPage(Math.ceil(data.length / rowsPerPage) - 1);
            }
        }, [data, rowsPerPage])

        const handleSave = () => {
            const created = data.filter(x => x.isNew);
            const updatedData = data.filter(x => updated.includes(x.id));

            setIsSaveDisabled(true);
            upsertMeasurementsReq.run(created, updatedData, deleted);
        }

        useImperativeHandle(ref, () => ({
            // Expose handleSave to parent component (AdminSettings)
            handleSave,
        }));

        const paginatedData = React.useMemo(() => {
            const start = page * rowsPerPage;
            return data.slice(start, start + rowsPerPage);
        }, [data, page, rowsPerPage]);

        const handleShowAll = () => {
            setData(prevData => {
                return [...prevData].sort((a, b) => {
                    if (showErrors) {
                        const aIsNew = a.isNew ? 1 : 0;
                        const bIsNew = b.isNew ? 1 : 0;
                        if (aIsNew !== bIsNew) return bIsNew - aIsNew;

                        return a.displayName?.localeCompare(b.displayName);
                    } else {
                        const aHasErrors = a.errors && a.errors.length > 0 ? 1 : 0;
                        const bHasErrors = b.errors && b.errors.length > 0 ? 1 : 0;
                        return bHasErrors - aHasErrors;
                    }
                });
            });

            if (!showErrors) {
                setPage(0);
            }

            setShowErrors(!showErrors);
        };

        const handleDeletePopUp = () => {
            if (selectedRows.length > 0) {
                setShowDeleteModal(true);
            }
        }

        const customHeaderComponent = () => (
            <FlexboxContainerComponent flexDirection='row' justifyContent='space-between' alignItems='center'>
                <IssuesSelector
                    errors={errors}
                    onSelect={handleShowAll}
                    visible={errors.length > 0}
                    getErrorText={getErrorText}
                    highlightErrors={showErrors}
                />
                <ReadOnlyWrapper readOnly={readonly}>
                    <Button
                        onClick={handleAdd}
                        isGhost
                        borderColor={Shade.shade550}
                        color={Background.navy}
                        padding='4px'
                        height='auto !important'
                        margin='0px 15px 0px 0px'
                        disabled={readonly}
                    >
                        + Add new unit
                    </Button>
                </ReadOnlyWrapper>
            </FlexboxContainerComponent>
        );

        const confirmDeleteModal = (
            <Modal
                showModal={showDeleteModal}
                onClose={() => setShowDeleteModal(false)}
                onClick={handleDelete}
                title="Remove unit of measurements?"
                primaryButtonText="Confirm"
                ghostButtonText="Back"
                primaryButtonColor={Main.brandDark}
                color={Main.black}
                borderColor={Main.disabled}
            >
                <p>{`Are you sure you want to delete selected unit of measurements?`}</p>
            </Modal>
        )

        if (getMeasurementsReq.isLoading) return <LoadingIndicator center />;

        return (
            <Page>
                <MultiHeaderTable
                    headers={MEASUREMENTS_HEADERS}
                    data={paginatedData}
                    orderBy={orderBy}
                    order={order}
                    onSelect={handleSelect}
                    onSelectAll={handleSelectAll}
                    onSort={handleSort}
                    selectedRows={selectedRows}
                    totalCount={data.length}
                    seletableTotalCount={data.filter(x => !x.disableDelete).length}
                    rowsPerPage={rowsPerPage}
                    currentPage={page}
                    setPage={setPage}
                    setRowsPerPage={setRowsPerPage}
                    getFieldName={getFieldName}
                    onEdit={handleChange}
                    onDelete={handleDeletePopUp}
                    customHeaderComponent={customHeaderComponent}
                    heightLightErrors={showErrors}
                    disabledDeleteToastMessage={CANNOT_SELECT_MEASUREMENT_UNIT}
                    maxHeight="75vh"
                    readOnly={readonly}
                />
                {confirmDeleteModal}
            </Page>
        );
    });

export default MeasurementsTable;
